import PropTypes from 'prop-types';
import React from 'react';
import { connectToStores, provideContext } from 'fluxible-addons-react';
import { handleHistory } from 'fluxible-router';
import ApplicationStore from '../stores/ApplicationStore';
import UserStore from '../stores/UserStore';
import DocumentStore from '../stores/DocumentStore';
import StaticContentStore from '../stores/StaticContentStore';
import AbStore from '../../client/stores/AbStore';
import ErrorBoundary from './ErrorBoundary';
import ErrorPage from './pages/ErrorPage';
import highlightCode from '../browser/highlightCode';
import Header from './Header';
import monitorContentHeight from '../util/monitorContentHeight';
import sendMessageToParentFrame from '../util/sendMessageToParentFrame';
import abTesting from './../browser/abTesting';

class Application extends React.Component {
  componentDidMount() {
    if (this.props.isFramedMode) {
      sendMessageToParentFrame({ type: 'ready', height: 500 });
      monitorContentHeight(height => sendMessageToParentFrame({ type: 'resize', height }));
    }
    highlightCode();

    abTesting(this.props.abExperiments);
  }

  componentDidUpdate(prevProps) {
    const newProps = this.props;
    if (newProps.pageTitle !== prevProps.pageTitle) {
      document.title = newProps.pageTitle;
    }
    if (newProps.currentRoute.url !== prevProps.currentRoute.url) {
      this.props.context.trackPage();
    }
    highlightCode();
  }

  getHandler() {
    const { doc, currentRoute, currentNavigateError } = this.props;

    if (currentRoute.name === 'errorPage') {
      return (<ErrorPage error={{
        message: currentRoute.query.errMsg,
        status: currentRoute.query.errStatus }}
      />);
    }

    if (!currentRoute) {
      const error = { message: 'Not Found', status: 404 };
      return <ErrorPage error={error} />;
    }

    if (doc && doc.err) {
      return <ErrorPage error={doc.err} />;
    }

    if (currentNavigateError) {
      return <ErrorPage error={currentNavigateError} />;
    }

    const Handler = currentRoute.handler;
    return (
      <ErrorBoundary>
        <Handler {...this.props} />
      </ErrorBoundary>
    );
  }

  render() {
    const {
      currentRoute,
      doc,
      fullWidth,
      isAuthenticated,
      isFramedMode,
      user
    } = this.props;

    // Temporary fix for: https://github.com/yahoo/fluxible-router/issues/108
    if (!currentRoute && typeof document !== 'undefined') {
      document.location = document.location;
    }

    let header;
    if (!isFramedMode) {
      header = (
        <Header
          theme="gray"
          isAuthenticated={isAuthenticated}
          currentRoute={currentRoute}
          fullWidth={fullWidth}
          user={user}
        />
      );
    }

    const styles = ['docs-application'];
    if (isFramedMode) styles.push('framed-mode');

    return (
      <div className={styles.join(' ')}>
        {header}
        {this.getHandler()}
      </div>
    );
  }
}


Application.propTypes = {
  doc: PropTypes.object,
  currentRoute: PropTypes.object.isRequired,
  fullWidth: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isFramedMode: PropTypes.bool.isRequired,
  pageTitle: PropTypes.string.isRequired,
  user: PropTypes.object
};

Application = connectToStores(
  Application,
  [ApplicationStore, DocumentStore, AbStore],
  (context, props) => {
    const appStore = context.getStore(ApplicationStore);
    const userStore = context.getStore(UserStore);
    const abStore = context.getStore(AbStore);
    let doc;
    let fullWidth = false;
    if (props.currentRoute) {
      doc = context.getStore(DocumentStore).getDocument(props.currentRoute.url);
      if (doc && doc.meta) fullWidth = !!doc.meta.fullWidth;
    }

    const staticContent = context.getStore(StaticContentStore).getContent();
    if (staticContent) {
      fullWidth = !!staticContent.meta.fullWidth;
    }

    return {
      doc,
      fullWidth,
      currentRoute: props.currentRoute,
      isAuthenticated: userStore.isAuthenticated(),
      isFramedMode: appStore.isFramedMode(),
      pageTitle: appStore.getPageTitle(),
      abExperiments: abStore.getAbExperiments(),
      user: userStore.getUser()
    };
  }
);

Application = provideContext(Application, {
  trackEvent: PropTypes.func.isRequired,
  trackPage: PropTypes.func.isRequired
});

Application = handleHistory(Application);

export default Application;
